import React, { useState, useEffect } from 'react';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import SchedulerHeader from './SchedulerHeader';
import SchedulerContent from './SchedulerContent';
import LoadingOverlay from './LoadingOverlay';
import { useSchedulerState } from '../hooks/useSchedulerState';
import { getCurrentSingaporeDate, parseDateString, formatDateToString } from '../lib/dateUtils';
import { CopyPasteProvider } from '@/contexts/CopyPasteContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

export type ViewType = 'daily' | 'weekly' | 'monthly' | 'report' | 'tasks' | 'admin' | 'patients' | 'inventory' | 'automation';

interface SchedulerProps {
  initialView?: ViewType;
  initialTab?: string;
}

const Scheduler = ({ initialView, initialTab }: SchedulerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentView, setCurrentView] = useState<ViewType>(initialView || 'daily');
  const [currentDate, setCurrentDate] = useState(getCurrentSingaporeDate());
  const [copiedItems, setCopiedItems] = useState<ScheduleItem[]>([]);
  const { toast } = useToast();

  // Global loading states for navigation
  const [isViewChanging, setIsViewChanging] = useState(false);
  const [targetView, setTargetView] = useState<ViewType | null>(null);

  // Update view based on route
  useEffect(() => {
    const topLevelPath = location.pathname.split('/')[1];
    if (['daily', 'weekly', 'monthly', 'report', 'tasks', 'admin', 'patients', 'inventory'].includes(topLevelPath)) {
      setCurrentView(topLevelPath as ViewType);
      // Clear view changing state after route updates
      setIsViewChanging(false);
    }
  }, [location.pathname]);

  // Handle view change by navigating to the appropriate route
  const handleViewChange = (view: ViewType) => {
    setIsViewChanging(true);
    setTargetView(view);
    navigate(`/${view}`);
    // Clear loading after minimum display time
    setTimeout(() => {
      setIsViewChanging(false);
      setTargetView(null);
    }, 300);
  };

  const {
    scheduleItems,
    appointments,
    tasks,
    doctors,
    staff,
    rooms,
    procedures,
    adminTasks,
    addScheduleItem,
    updateScheduleItem,
    deleteScheduleItem,
    deleteScheduleSeries,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    addTask,
    updateTask,
    deleteTask,
    updateDoctor,
    updateStaff,
    updateProcedure,
    updateAdminTask,
    addDoctor,
    addStaff,
    addProcedure,
    addAdminTask,
    getAppointmentForScheduleItem,
    getTaskForScheduleItem,
    staffTodos,
    addStaffTodo,
    updateStaffTodo,
    deleteStaffTodo,
    isCreatingScheduleItem,
    isUpdatingScheduleItem,
    isDeletingScheduleItem,
    isLoadingSchedules,
    isFetchingSchedules,
    // Lazy loading for monthly view
    loadFullDaySchedule,
    fullyLoadedDays,
    dayScheduleCache,
    loadingDays,
    // Refresh function for external triggers (e.g., Plato sync)
    refreshScheduleItems,
  } = useSchedulerState(currentView, currentDate);

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    
    if (currentView === 'daily') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (currentView === 'weekly' || currentView === 'report') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    
    setCurrentDate(newDate);
  };

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const copyScheduleItems = (date: string) => {
    const itemsForDate = scheduleItems.filter(item => item.date === date);
    setCopiedItems(itemsForDate);
  };

  const pasteScheduleItems = (targetDate: string) => {
    if (copiedItems.length === 0) return;

    // Existing items on the target date for duplicate checks
    const existingItemsForTargetDate = scheduleItems.filter(
      (existing) => existing.date === targetDate
    );

    const isDuplicateOnTarget = (candidate: Omit<ScheduleItem, 'id'>) => {
      return existingItemsForTargetDate.some((existing) => {
        const sameType = existing.type === candidate.type;
        const sameTimeWindow =
          existing.startTime === candidate.startTime &&
          existing.endTime === candidate.endTime;

        const sameDoctor = (existing.doctorId || undefined) === (candidate.doctorId || undefined);
        const sameStaff = (existing.staffId || undefined) === (candidate.staffId || undefined);

        // Consider duplicate when same assignment (doctor/staff), same time window, and same type
        return sameType && sameTimeWindow && sameDoctor && sameStaff;
      });
    };

    let createdCount = 0;
    let skippedCount = 0;

    copiedItems.forEach((item) => {
      // Strip metadata and set new date. Let addScheduleItem assign clinic and timestamps.
      const { id: _omitId, created_at: _omitCreated, updated_at: _omitUpdated, clinic_id: _omitClinic, endDate: _omitEndDate, ...rest } = item;

      // For multi-day items, shift the end date by the same day-offset as the start date
      // (keeping the original endDate would produce a backwards range on the paste target).
      let newEndDate: string | undefined;
      if (item.endDate && item.endDate !== item.date) {
        const durationDays = Math.round(
          (parseDateString(item.endDate).getTime() - parseDateString(item.date).getTime()) / (1000 * 60 * 60 * 24)
        );
        if (durationDays > 0) {
          const shiftedEnd = parseDateString(targetDate);
          shiftedEnd.setDate(shiftedEnd.getDate() + durationDays);
          newEndDate = formatDateToString(shiftedEnd);
        }
      }

      const candidate: Omit<ScheduleItem, 'id'> = {
        ...rest,
        date: targetDate,
        ...(newEndDate ? { endDate: newEndDate } : {}),
      };

      if (isDuplicateOnTarget(candidate)) {
        skippedCount += 1;
        return;
      }

      addScheduleItem(candidate);
      createdCount += 1;
    });

    if (createdCount > 0 || skippedCount > 0) {
      const parts: string[] = [];
      if (createdCount > 0) parts.push(`${createdCount} created`);
      if (skippedCount > 0) parts.push(`${skippedCount} skipped as duplicates`);
      toast({ title: 'Paste complete', description: parts.join(', ') });
    }
  };

  const activeDoctors = doctors.filter(d => d.status === 'active');
  const activeStaff = staff.filter(s => s.status === 'active');

  // Determine loading message based on operation
  const getLoadingMessage = () => {
    if (isViewChanging && targetView) {
      const viewNames = {
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        report: 'Report',
        tasks: 'Tasks',
        admin: 'Admin',
        patients: 'Patients',
        inventory: 'Inventory',
      };
      return `Switching to ${viewNames[targetView]} view...`;
    }
    if (isCreatingScheduleItem) return 'Creating schedule item...';
    if (isUpdatingScheduleItem) return 'Updating schedule item...';
    if (isDeletingScheduleItem) return 'Deleting schedule item...';
    return 'Processing...';
  };

  const isLoading = isViewChanging || isCreatingScheduleItem || isUpdatingScheduleItem || isDeletingScheduleItem;

  return (
    <CopyPasteProvider>
      <div className="min-h-screen bg-background">
        <LoadingOverlay isLoading={isLoading} message={getLoadingMessage()} />
        <SchedulerHeader
          currentView={currentView}
          onViewChange={handleViewChange}
          currentDate={currentDate}
          onNavigate={navigateDate}
          onDateChange={handleDateChange}
          copiedItemsCount={copiedItems.length}
          isLoading={isLoading}
        />
        
        <div className="w-full px-2 py-3 sm:px-4 sm:py-6">
          <SchedulerContent
            currentView={currentView}
            currentDate={currentDate}
            initialTab={initialTab}
            scheduleItems={scheduleItems}
            appointments={appointments}
            tasks={tasks}
            doctors={activeDoctors}
            staff={activeStaff}
            allDoctors={doctors}
            allStaff={staff}
            procedures={procedures}
            adminTasks={adminTasks}
            rooms={rooms}
            onAddScheduleItem={addScheduleItem}
            onUpdateScheduleItem={updateScheduleItem}
            onDeleteScheduleItem={deleteScheduleItem}
            onDeleteScheduleSeries={deleteScheduleSeries}
            onAddAppointment={addAppointment}
            onUpdateAppointment={updateAppointment}
            onDeleteAppointment={deleteAppointment}
            onAddTask={addTask}
            onUpdateTask={updateTask}
            onDeleteTask={deleteTask}
            onUpdateDoctor={updateDoctor}
            onUpdateStaff={updateStaff}
            onUpdateProcedure={updateProcedure}
            onUpdateAdminTask={updateAdminTask}
            onAddDoctor={addDoctor}
            onAddStaff={addStaff}
            onAddProcedure={addProcedure}
            onAddAdminTask={addAdminTask}
            getAppointmentForScheduleItem={getAppointmentForScheduleItem}
            getTaskForScheduleItem={getTaskForScheduleItem}
            onCopyScheduleItems={copyScheduleItems}
            onPasteScheduleItems={pasteScheduleItems}
            copiedItems={copiedItems}
            onDateChange={handleDateChange}
            onViewChange={handleViewChange}
            staffTodos={staffTodos}
            onAddStaffTodo={addStaffTodo}
            onUpdateStaffTodo={updateStaffTodo}
            onDeleteStaffTodo={deleteStaffTodo}
            // Lazy loading for monthly view
            loadFullDaySchedule={loadFullDaySchedule}
            fullyLoadedDays={fullyLoadedDays}
            dayScheduleCache={dayScheduleCache}
            loadingDays={loadingDays}
            // Loading states
            isLoadingSchedules={isLoadingSchedules}
            isFetchingSchedules={isFetchingSchedules}
            // Refresh function for external triggers (e.g., Plato sync)
            onRefreshSchedule={refreshScheduleItems}
          />
        </div>
      </div>
    </CopyPasteProvider>
  );
};

export default Scheduler;
